import { userAtom } from '@/global-store';
import { getEyeAbleUrl } from '@/helpers/functions';
import { Heart } from 'lucide-react';
import { useAtom } from 'jotai';
import { RouterLink } from '@/components/RouterLink';
import { cn } from '@helpers/cn';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

interface FooterProps {
  isColumns?: boolean;
}

const newTab = { target: '_blank', rel: 'noopener noreferrer' };

const Footer = ({ isColumns }: FooterProps) => {
  const { t, i18n } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const language = i18n.resolvedLanguage ?? 'en';
  const websiteUrl = (key: 'EAmission' | 'EAblog') =>
    getEyeAbleUrl(key, language) ?? 'https://eye-able.com';

  const links = [
    ...(user
      ? [{ to: '/page-overview', name: t('pageOverview'), external: false }]
      : []),
    {
      to: 'https://status.eye-able.com',
      name: t('status'),
      description: t('uptime'),
      external: true,
    },
    { to: websiteUrl('EAmission'), name: t('footerUs'), external: true },
    { to: websiteUrl('EAblog'), name: t('footerBlog'), external: true },
    { to: '/privacy-policy', name: t('footerDataPrivacy'), external: !user },
  ];

  return (
    <footer
      id='dashboard-footer'
      tabIndex={-1}
      role='contentinfo'
      className={cn(
        'eye:flex eye:items-center eye:justify-between eye:mt-auto eye:w-full eye:gap-3xs eye:text-14 eye:pt-m eye:outline-none',
        isColumns ? 'eye:flex-col' : 'eye:flex-col eye:xl:flex-row',
      )}
    >
      <div>
        &copy; {new Date().getFullYear()}, made with
        <Heart
          aria-label={t('love')}
          fill='black'
          strokeWidth={0}
          className='eye:inline eye:h-[14px] eye:w-[14px] eye:mx-5xs eye:-mt-4'
        />
        by{' '}
        <a
          href='https://eye-able.com'
          className='eye:font-medium eye:hover:underline'
          {...newTab}
        >
          Web Inclusion GmbH
        </a>{' '}
        for a better web.
      </div>
      <nav aria-label={t('footerNavigation')}>
        <ul className='eye:flex eye:flex-wrap eye:gap-2xs'>
          {links.map((link) => (
            <li key={link.to}>
              <RouterLink
                to={link.to}
                title={link.description}
                className='eye:hover:underline'
                {...(link.external && newTab)}
              >
                {link.name}
              </RouterLink>
            </li>
          ))}
        </ul>
      </nav>
    </footer>
  );
};

export default Footer;
